import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { FileText, Sparkles, IndianRupee, Sprout, TrendingUp, HandHeart, UserX, RefreshCw, Scale, ArrowRight } from "lucide-react";

const Terms = () => {
  const { t } = useLang();
  const l = t.legal;

  const sections = [
    { icon: FileText, title: l.terms_s1_title, body: l.terms_s1 },
    { icon: Sparkles, title: l.terms_s2_title, body: l.terms_s2, highlight: true },
    { icon: IndianRupee, title: l.terms_s3_title, body: l.terms_s3, highlight: true },
    { icon: HandHeart, title: l.terms_s4_title, body: l.terms_s4 },
    { icon: TrendingUp, title: l.terms_s5_title, body: l.terms_s5 },
    { icon: UserX, title: l.terms_s6_title, body: l.terms_s6 },
    { icon: RefreshCw, title: l.terms_s7_title, body: l.terms_s7 },
    { icon: Scale, title: l.terms_s8_title, body: l.terms_s8 },
  ];

  return (
    <div data-testid="terms-page" className="bg-[#FDFBF7]">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-stone-200 bg-gradient-to-b from-[#F4F1EA] to-[#FDFBF7]">
        <div className="container-gc px-4 sm:px-6 py-14 sm:py-20 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-stone-200 text-xs font-medium text-[#1E4D2B] mb-5">
              <Sprout size={14} /> {t.brand}
            </div>
            <h1 data-testid="terms-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-stone-900 leading-tight">
              {l.terms_title}
            </h1>
            <p className="mt-5 text-lg text-stone-700 leading-relaxed max-w-2xl">{l.terms_intro}</p>
            <div className="mt-4 text-sm text-stone-500">{l.last_updated}</div>
          </div>
        </div>
      </section>

      {/* Pricing Callout Banner */}
      <section className="container-gc px-4 sm:px-6 pt-10">
        <div data-testid="pricing-banner" className="relative overflow-hidden rounded-3xl bg-[#1E4D2B] text-white p-6 sm:p-8">
          <div className="absolute -right-8 -top-10 w-52 h-52 rounded-full bg-white/5" />
          <div className="absolute -left-12 -bottom-16 w-64 h-64 rounded-full bg-white/5" />
          <div className="relative grid sm:grid-cols-2 gap-5 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#F59E0B] mb-2">
                <Sparkles size={14} /> Pricing at a Glance
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold leading-tight">1-Month Free · Then ₹30/month</h2>
              <p className="mt-2 text-white/80 text-sm sm:text-base leading-relaxed">
                Start free for 30 days. Continue for just ₹30/month afterwards. Cancel anytime.
              </p>
            </div>
            <div className="flex sm:justify-end">
              <div className="inline-flex flex-col items-start sm:items-end gap-2">
                <div className="flex items-baseline gap-1">
                  <span className="text-5xl font-bold text-[#F59E0B]">₹30</span>
                  <span className="text-white/70">/month</span>
                </div>
                <div className="text-xs text-white/70">after 1-month free trial</div>
                <Link
                  to="/login"
                  data-testid="terms-signup-cta"
                  className="mt-1 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold text-sm transition-colors"
                >
                  Start Free Trial <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sections */}
      <section className="section-pad !pt-10">
        <div className="container-gc max-w-4xl grid gap-5">
          {sections.map((s, i) => {
            const Icon = s.icon;
            return (
              <article
                key={i}
                data-testid={`terms-section-${i}`}
                className={`card-gc p-6 sm:p-7 flex flex-col sm:flex-row gap-5 hover:shadow-md transition-shadow ${
                  s.highlight ? "border-[#F59E0B]/40 bg-[#FFF8EB]" : ""
                }`}
              >
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    s.highlight ? "bg-[#F59E0B] text-white" : "bg-[#1E4D2B] text-white"
                  }`}
                >
                  <Icon size={22} />
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl font-bold text-stone-900">{s.title}</h2>
                  <p className="mt-2 text-stone-700 leading-relaxed">{s.body}</p>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default Terms;
